import styled, { css, keyframes } from 'styled-components';
import {
    robotoRegular,
    scarpaGray100,
    scarpaGray200,
    scarpaGray25,
    scarpaGray400,
    scarpaGray500,
    scarpaGray800,
    scarpaGray900,
    spanishViridian25,
    spanishViridian500,
    white,
} from '@habitech/shared';
import { Wrapper } from '../calculo-sugeridos/styles';

/**
 * Raíz de la sección de Asignaciones: reusa el Wrapper compartido pero aplica la
 * tipografía Roboto (igual que el carrito comercial). font-family se hereda, así
 * que con fijarla en la raíz basta; los íconos (pcom-icon) conservan su propia
 * fuente porque la definen vía su clase.
 */
export const AssignmentsRoot = styled(Wrapper)`
    font-family: ${robotoRegular};
`;

/** Acentos por categoría (no existen como tokens en @habitech/shared). */
export const PRIME_ACCENT = '#3B82F6';
export const PRIME_ACCENT_SOFT = '#EFF6FF';
export const DESTACADO_ACCENT = '#E9B44A';
export const DESTACADO_ACCENT_SOFT = '#fdf3e1';
const DANGER = '#d32f2f';

export const Board = styled.div`
    display: grid;
    grid-template-columns: minmax(280px, 320px) 1fr;
    gap: 16px;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const PanelCard = styled.div`
    background: ${white};
    border: 1px solid ${scarpaGray200};
    border-radius: 14px;
    padding: 16px;
`;

/**
 * Variante del panel de Propiedades que permanece visible (sticky) al hacer
 * scroll, para poder arrastrar propiedades hacia cualquier categoría de slots
 * (Prime/Destacados) sin perder de vista el listado. En pantallas angostas
 * (una sola columna) vuelve a comportarse de forma normal.
 */
export const PropertiesPanelCard = styled(PanelCard)`
    position: sticky;
    top: 16px;
    align-self: start;
    max-height: calc(100vh - 32px);
    overflow: auto;

    @media (max-width: 900px) {
        position: static;
        top: auto;
        max-height: none;
        overflow: visible;
    }
`;

/* ───────────────────────── Propiedades ───────────────────────── */

export const PanelTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

export const PanelTitle = styled.h3`
    font-size: 15px;
    font-weight: 700;
    color: ${scarpaGray900};
    margin: 0;
`;

export const CountPill = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${scarpaGray500};
    background: ${scarpaGray25};
    border: 1px solid ${scarpaGray200};
    border-radius: 999px;
    padding: 2px 10px;
`;

export const SearchBox = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid ${scarpaGray200};
    border-radius: 10px;
    padding: 8px 10px;
    margin-bottom: 12px;
    color: ${scarpaGray400};

    input {
        border: 0;
        outline: 0;
        flex: 1;
        font-family: inherit;
        font-size: 13px;
        color: ${scarpaGray900};
        background: transparent;

        &::placeholder {
            color: ${scarpaGray400};
        }
    }
`;

export const PropertiesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 560px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 2px 4px;
`;

export const PropertyItem = styled.button<{ $selected?: boolean; $free?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
    min-width: 0;
    background: ${white};
    border: 1px solid ${({ $selected }) => ($selected ? PRIME_ACCENT : scarpaGray200)};
    box-shadow: ${({ $selected }) => ($selected ? `0 0 0 2px ${PRIME_ACCENT_SOFT}` : 'none')};
    border-radius: 12px;
    padding: 10px 12px;
    cursor: ${({ $free }) => ($free ? 'grab' : 'default')};
    transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

    &:active {
        cursor: ${({ $free }) => ($free ? 'grabbing' : 'default')};
    }

    .property-name {
        font-size: 13px;
        font-weight: 700;
        color: ${scarpaGray900};
    }

    .property-meta {
        font-size: 11px;
        color: ${scarpaGray500};
    }
`;

export const Badge = styled.span<{ $variant: 'libre' | 'prime' | 'destacado' | 'asignada' }>`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    font-size: 11px;
    font-weight: 700;
    border-radius: 999px;
    padding: 2px 8px;
    margin-top: 2px;

    ${({ $variant }) => {
        switch ($variant) {
            case 'libre':
                return css`
                    color: ${spanishViridian500};
                    background: ${spanishViridian25};
                `;
            case 'prime':
                return css`
                    color: ${PRIME_ACCENT};
                    background: ${PRIME_ACCENT_SOFT};
                `;
            case 'destacado':
                return css`
                    color: ${DESTACADO_ACCENT};
                    background: ${DESTACADO_ACCENT_SOFT};
                `;
            default:
                return css`
                    color: ${scarpaGray800};
                    background: ${scarpaGray100};
                `;
        }
    }}
`;

export const SelectAllRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: ${scarpaGray800};
    margin-bottom: 12px;
    cursor: pointer;
`;

/** Fila "Seleccionar todas (página actual)" + "Limpiar (N)". */
export const PropertyToolbarRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
`;

export const ClearLink = styled.button`
    border: 0;
    background: transparent;
    color: ${spanishViridian500};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;

    &:disabled {
        color: ${scarpaGray400};
        cursor: default;
    }
`;

/** Fila superior de la tarjeta de propiedad (checkbox + nombre). */
export const PropertyRowTop = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    input[type='checkbox'] {
        cursor: pointer;
    }
`;

/** Lado izquierdo de la cabecera de "Ocupados": select-all + "Limpiar selección". */
export const OccupiedHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;

    ${ClearLink} {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }
`;

/* ───────────────── Barra de selección múltiple ───────────────── */

export const SelectionBar = styled.div<{ $large?: boolean }>`
    position: sticky;
    bottom: 12px;
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    background: ${white};
    border: 1px solid ${scarpaGray200};
    border-radius: 14px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
    padding: 12px 16px;
    z-index: 5;

    ${({ $large }) =>
        $large &&
        css`
            padding: 18px 24px;
            border-radius: 16px;

            .title {
                font-size: 22px;
            }

            .subtitle {
                font-size: 17px;
            }

            button {
                font-size: 17px;
            }
        `}
`;

export const SelectionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const SelectionCount = styled.span<{ $tone?: 'prime' | 'dark'; $large?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${({ $large }) => ($large ? '46px' : '34px')};
    height: ${({ $large }) => ($large ? '46px' : '34px')};
    padding: 0 8px;
    border-radius: 999px;
    background: ${({ $tone }) => ($tone === 'dark' ? '#1f2024' : PRIME_ACCENT)};
    color: ${white};
    font-weight: 800;
    font-size: ${({ $large }) => ($large ? '18px' : '14px')};
`;

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

/** Spinner en línea para botones de acción (hereda el color del texto). */
export const InlineSpinner = styled.span`
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    flex-shrink: 0;
`;

/** Botones de texto de la barra inferior de slots ocupados ("Cancelar" / "Liberar slots"). */
export const LiberateBarButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: ${scarpaGray500};
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
        color: ${scarpaGray900};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const SelectionTexts = styled.div`
    display: flex;
    flex-direction: column;

    .title {
        font-size: 14px;
        font-weight: 700;
        color: ${scarpaGray900};
    }

    .subtitle {
        font-size: 12px;
        color: ${scarpaGray500};
    }
`;

export const SelectionActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

/** CTA de asignación como botón de texto neutro con ícono (corona/estrella). */
export const AssignCtaButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    color: ${scarpaGray900};
    cursor: pointer;
    transition: color 0.15s ease;

    .cta-icon {
        font-size: 16px;
        line-height: 1;
    }

    &:hover {
        color: ${spanishViridian500};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

/* ───────────────────────── Slots board ───────────────────────── */

export const SlotsColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const CategoryCard = styled(PanelCard)<{ $category: 'prime' | 'destacado' }>`
    border-left: 4px solid
        ${({ $category }) => ($category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT)};
`;

export const CategoryHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

export const CategoryHeading = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const CategoryIcon = styled.span<{ $category: 'prime' | 'destacado' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    color: ${({ $category }) => ($category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT)};
    background: ${({ $category }) =>
        $category === 'prime' ? PRIME_ACCENT_SOFT : DESTACADO_ACCENT_SOFT};

    .crown-glyph {
        font-size: 18px;
        line-height: 1;
    }
`;

export const CategoryTitle = styled.div<{ $category: 'prime' | 'destacado' }>`
    font-size: 16px;
    font-weight: 800;
    color: ${({ $category }) => ($category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT)};
    line-height: 1.1;

    span {
        display: block;
        font-size: 12px;
        font-weight: 500;
        color: ${scarpaGray500};
    }
`;

export const ProgressTrack = styled.div`
    height: 6px;
    border-radius: 999px;
    background: ${scarpaGray100};
    overflow: hidden;
    margin-bottom: 14px;
`;

export const ProgressFill = styled.div<{ $ratio: number; $category: 'prime' | 'destacado' }>`
    height: 100%;
    width: ${({ $ratio }) => Math.max(0, Math.min(100, $ratio * 100))}%;
    background: ${({ $category }) => ($category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT)};
    transition: width 0.2s ease;
`;

export const GroupLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 700;
    color: ${scarpaGray500};
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin: 14px 0 8px;
`;

export const SlotGrid = styled.div<{ $cols?: number }>`
    display: grid;
    grid-template-columns: ${({ $cols }) =>
        $cols ? `repeat(${$cols}, minmax(0, 1fr))` : 'repeat(auto-fill, minmax(190px, 1fr))'};
    gap: 10px;
`;

/** Color de acento según la categoría del slot (evita ternarios anidados en SlotCard). */
const categoryAccent = (category: 'prime' | 'destacado'): string =>
    category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT;

/** Variante suave del acento por categoría (para el halo del slot seleccionado). */
const categoryAccentSoft = (category: 'prime' | 'destacado'): string =>
    category === 'prime' ? PRIME_ACCENT_SOFT : DESTACADO_ACCENT_SOFT;

export const SlotCard = styled.div<{
    $selected?: boolean;
    $category: 'prime' | 'destacado';
    /** Activo cuando se arrastra una propiedad libre encima (escenario reemplazo). */
    $replaceActive?: boolean;
}>`
    position: relative;
    border: ${({ $replaceActive }) => ($replaceActive ? '2px dashed' : '1px solid')}
        ${({ $selected, $replaceActive, $category }) =>
            $selected || $replaceActive ? categoryAccent($category) : scarpaGray200};
    box-shadow: ${({ $selected, $replaceActive, $category }) =>
        $selected || $replaceActive ? `0 0 0 3px ${categoryAccentSoft($category)}` : 'none'};
    background: ${({ $replaceActive, $category }) =>
        $replaceActive ? categoryAccentSoft($category) : white};
    border-radius: 12px;
    padding: 10px 12px;
    min-height: 86px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

    .slot-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
    }

    .slot-index {
        font-size: 11px;
        font-weight: 700;
        color: ${scarpaGray400};
    }

    .slot-property {
        font-size: 13px;
        font-weight: 700;
        color: ${scarpaGray900};
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
    }

    .slot-name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        min-width: 0;
    }

    .slot-meta {
        font-size: 11px;
        color: ${scarpaGray500};
        margin-top: 2px;
    }

    .slot-status {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        color: ${scarpaGray500};
        margin-top: 6px;
    }

    .dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${spanishViridian500};
        flex: none;
    }

    .dot.warn {
        background: ${DESTACADO_ACCENT};
    }
`;

/**
 * Pastilla "Reemplazar propiedad" que aparece sobre un slot ocupado cuando se
 * arrastra una propiedad libre encima. Usa el acento de la categoría del slot.
 */
export const ReplaceBadge = styled.span<{ $category: 'prime' | 'destacado' }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 4;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    background: ${({ $category }) => categoryAccent($category)};
    color: ${white};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
    pointer-events: none;
`;

export const SlotSelect = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const KebabButton = styled.button`
    border: 0;
    background: transparent;
    cursor: pointer;
    color: ${scarpaGray500};
    padding: 2px 4px;
    border-radius: 6px;
    line-height: 0;

    &:hover {
        background: ${scarpaGray25};
        color: ${scarpaGray900};
    }
`;

export const MenuWrapper = styled.div`
    position: relative;
`;

export const MenuPopover = styled.div`
    position: absolute;
    top: 26px;
    right: 0;
    z-index: 5;
    background: ${white};
    border: 1px solid ${scarpaGray200};
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    min-width: 160px;
    overflow: hidden;
`;

export const MenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: ${DANGER};
    padding: 10px 12px;
    cursor: pointer;

    &:hover {
        background: #fdeaea;
    }
`;

export const AvailableCard = styled.button<{
    $active?: boolean;
    $category: 'prime' | 'destacado';
}>`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    gap: 6px;
    min-height: 118px;
    padding: 10px 12px;
    border-radius: 12px;
    border: ${({ $active }) => ($active ? '2px solid' : '1px dashed')}
        ${({ $active, $category }) => ($active ? categoryAccent($category) : scarpaGray200)};
    background: ${({ $active, $category }) =>
        $active ? categoryAccentSoft($category) : white};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${({ $category }) =>
            $category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT};
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    }

    &:disabled {
        cursor: default;
    }

    .slot-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }

    .slot-num {
        font-size: 12px;
        font-weight: 700;
        color: ${scarpaGray900};
    }

    .slot-id {
        font-size: 10px;
        font-weight: 500;
        color: ${scarpaGray400};
    }

    .slot-state {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: ${scarpaGray400};
    }

    .slot-center {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
    }

    .plus {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        line-height: 1;
        color: ${({ $category }) => ($category === 'prime' ? PRIME_ACCENT : DESTACADO_ACCENT)};
        background: ${({ $category }) => ($category === 'prime' ? '#C9DCFB' : '#F6E2A8')};
    }

    .label {
        font-size: 12px;
        font-weight: 700;
        color: ${scarpaGray900};
    }

    .vence {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 500;
        color: ${scarpaGray500};
        text-align: center;
    }
`;

export const EmptyGroup = styled.div`
    font-size: 12px;
    color: ${scarpaGray400};
    padding: 8px 2px;
`;

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
`;

export const HintBanner = styled.div<{ $variant?: 'info' | 'warn' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${robotoRegular};
    font-size: 14px;
    font-weight: ${({ $variant }) => ($variant === 'warn' ? 700 : 500)};
    color: ${({ $variant }) => ($variant === 'warn' ? DESTACADO_ACCENT : spanishViridian500)};
    background: ${({ $variant }) =>
        $variant === 'warn' ? DESTACADO_ACCENT_SOFT : spanishViridian25};
    border: 1px solid
        ${({ $variant }) => ($variant === 'warn' ? DESTACADO_ACCENT : spanishViridian500)};
    border-radius: 10px;
    padding: 12px 14px;
`;

export const SaveError = styled.div`
    color: ${DANGER};
    font-size: 13px;
    margin-top: 12px;
`;

/* ───────────────────── Modal de confirmación ───────────────────── */

/** Fondo oscuro del modal compacto de liberación. */
export const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(0, 0, 0, 0.55);
`;

/** Tarjeta del modal compacto (ancho acotado para no dejar espacio sobrante). */
export const ModalCard = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;
    background: ${white};
    border-radius: 12px;
    padding: 22px 22px 18px;
    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.24);
`;

export const ModalClose = styled.button`
    position: absolute;
    top: 14px;
    right: 14px;
    display: inline-flex;
    border: 0;
    background: transparent;
    color: ${scarpaGray500};
    cursor: pointer;
    padding: 2px;

    &:hover {
        color: ${scarpaGray900};
    }
`;

export const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    h4 {
        margin: 0;
        padding-right: 24px;
        font-size: 17px;
        font-weight: 700;
        color: ${scarpaGray900};
    }

    p {
        margin: 0;
        font-size: 13px;
        line-height: 1.45;
        color: ${scarpaGray500};
    }

    label {
        width: 100%;
        margin-top: 4px;
        font-size: 13px;
        font-weight: 600;
        color: ${scarpaGray900};
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    textarea {
        width: 100%;
        font-family: inherit;
        font-size: 13px;
        color: ${scarpaGray900};
        padding: 8px 10px;
        border: 1px solid ${scarpaGray200};
        border-radius: 8px;
        outline: none;
        resize: vertical;
        min-height: 52px;
        box-sizing: border-box;

        &:focus {
            border-color: ${spanishViridian500};
        }
    }
`;

export const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 14px;
    margin-top: 14px;
`;

/* ──────────────── Modal de reemplazo (ACTUAL vs NUEVA) ──────────────── */

export const ReplaceCompare = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 6px;
`;

/**
 * Fila comparativa: 'current' (propiedad saliente) o 'new' (propiedad entrante).
 * La variante 'new' usa el acento de la categoría del slot (azul Prime / ámbar
 * Destacados) para el borde, el fondo suave y la etiqueta.
 */
export const ReplaceRow = styled.div<{
    $variant: 'current' | 'new';
    $category: 'prime' | 'destacado';
}>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid
        ${({ $variant, $category }) =>
            $variant === 'new' ? categoryAccent($category) : scarpaGray200};
    background: ${({ $variant, $category }) =>
        $variant === 'new' ? categoryAccentSoft($category) : white};

    .role {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: ${({ $variant, $category }) =>
            $variant === 'new' ? categoryAccent($category) : scarpaGray400};
    }

    .prop {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }

    .info {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .name {
        font-size: 14px;
        font-weight: 700;
        color: ${scarpaGray900};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-decoration: ${({ $variant }) => ($variant === 'current' ? 'line-through' : 'none')};
        opacity: ${({ $variant }) => ($variant === 'current' ? 0.65 : 1)};
    }

    .id {
        font-size: 12px;
        color: ${scarpaGray500};
    }
`;

/** Miniatura placeholder de la propiedad (no hay URL de imagen en el portafolio). */
export const ReplaceThumb = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 36px;
    flex: none;
    border-radius: 8px;
    background: ${scarpaGray100};
    color: ${scarpaGray400};
`;

/** Botón con borde para las acciones del modal de reemplazo (Cancelar / Reemplazar). */
export const ModalOutlineButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid ${scarpaGray200};
    background: ${white};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: ${scarpaGray900};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
        border-color: ${PRIME_ACCENT};
        color: ${PRIME_ACCENT};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

/** Toast inferior para confirmar asignaciones y liberaciones. */
export const Toast = styled.div`
    position: fixed;
    left: 50%;
    bottom: 28px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 18px;
    max-width: calc(100vw - 32px);
    background: #1f2024;
    color: ${white};
    padding: 12px 18px;
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28);
    font-size: 14px;
    z-index: 1000;
`;

/** Campo de filtro de propiedades (etiqueta + select), estilo del diseño. */
export const FilterField = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;

    .filter-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: ${scarpaGray500};
    }

    select {
        font-family: inherit;
        font-size: 14px;
        color: ${scarpaGray900};
        padding: 10px 12px;
        border: 1px solid ${scarpaGray200};
        border-radius: 10px;
        background: ${white};
        outline: none;
        cursor: pointer;
        transition: border-color 0.15s ease;

        &:focus {
            border-color: ${spanishViridian500};
        }
    }
`;

/* ───────────────── Paginación ───────────────── */

export const PagerBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
    font-size: 12px;
    color: ${scarpaGray500};
`;

export const PagerInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    select {
        font-family: inherit;
        font-size: 12px;
        color: ${scarpaGray900};
        padding: 4px 6px;
        border: 1px solid ${scarpaGray200};
        border-radius: 8px;
        outline: none;
        cursor: pointer;

        &:focus {
            border-color: ${spanishViridian500};
        }
    }
`;

export const PagerNav = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const PageButton = styled.button<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 6px;
    border-radius: 8px;
    border: 1px solid ${({ $active }) => ($active ? spanishViridian500 : scarpaGray200)};
    background: ${({ $active }) => ($active ? spanishViridian500 : white)};
    color: ${({ $active }) => ($active ? white : scarpaGray900)};
    font-family: inherit;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        border-color: ${spanishViridian500};
        color: ${({ $active }) => ($active ? white : spanishViridian500)};
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;
